import { useQuery } from "@tanstack/react-query";
import { apiService } from "../../services/apiService";
import { CursorTooltip } from "../CursorTooltip";
import { StatusCell } from "../molecules/StatusCell";
import { TextCell } from "../molecules/TextCell";
import { ResidentNameCell } from "../molecules/ResidentNameCell";
import { ResidentHoverCard } from "../molecules/ResidentHoverCard";
import { DaysOpenCell } from "../molecules/DaysOpenCell";
import { ResidentData } from "../../types/dashboard";
import { WorkItem } from "../../types/workItems";
import { useApiClient } from "../../contexts/ApiClientContext";
import { EllipsisVertical, Ban } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "../ui/dropdown-menu";

export interface CareRowClickData {
  row: ResidentData;
  fullWorkItem?: WorkItem;
}

interface CareAssessmentRowProps {
  row: ResidentData;
  index: number;
  isLastRow: boolean;
  onClick: (data: CareRowClickData) => void;
  onDismiss: (row: ResidentData) => void;
  canDismiss?: boolean;
  style?: React.CSSProperties;
}

export function CareAssessmentRow({ row, index, isLastRow, onClick, onDismiss, canDismiss = false, style }: CareAssessmentRowProps) {
  // Get API client from context (widget) or use default (main app)
  const apiClient = useApiClient();

  // Fetch full work item details if work_item_id is available
  const { data: fullWorkItem } = useQuery({
    queryKey: ["work-item", row.work_item_id],
    queryFn: async () => {
      // Use widget API client if available, otherwise use main app API
      if (apiClient) {
        return apiClient.getWorkItem(row.work_item_id!);
      } else {
        return apiService.getSingleWorkItem(row.work_item_id!);
      }
    },
    enabled: !!row.work_item_id,
    staleTime: 60 * 1000, // Match auto-refresh interval
    gcTime: 10 * 60 * 1000, // 10 minutes
  });

  const isQueued = row.statusNormalized === "Queued";
  const showDismissMenu = isQueued && canDismiss;

  // NETSOL-12075: Date of Birth, Location, Admission Date and Payor are no
  // longer grid columns — they surface in this row hover-over card instead,
  // matching the MDS tab's hover.
  const hoverDetails = (
    <ResidentHoverCard
      residentName={row.residentName}
      rows={[
        { label: "Date of Birth", value: row.dateOfBirth },
        { label: "Admission Date", value: row.admissionDate },
        { label: "Location", value: row.location },
        { label: "Payor", value: row.payor },
      ]}
    />
  );

  return (
    <CursorTooltip
      key={`${row.residentName}-${index}`}
      content={hoverDetails}
      onClick={() => onClick({ row, fullWorkItem })}
      className={`border-b border-border cursor-pointer transition-colors hover:bg-muted/50 ${
        isLastRow ? "border-b-0" : ""
      }`}
      style={style}
    >
      {/* Action Menu - visible only when status is Queued and user has dismiss permission */}
      <td className="p-1 text-center" onClick={(e) => e.stopPropagation()}>
        {showDismissMenu && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="p-1 rounded hover:bg-muted">
                <EllipsisVertical className="h-4 w-4 text-muted-foreground" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="min-w-[180px]">
              <DropdownMenuItem
                onSelect={() => onDismiss(row)}
                className="text-destructive focus:text-destructive"
              >
                <Ban className="h-4 w-4 mr-2" />
                Dismiss
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </td>

      {/* Care Assessment columns (NETSOL-12075: DOB/Location/Admission Date/Payor
          moved to the row hover-over above). Cell order MUST match CARE_COLUMNS
          in CareAssessmentsTable. */}
      <StatusCell status={row.statusNormalized} statusBucket={row.statusBucket} />
      <ResidentNameCell residentName={row.residentName} />
      <TextCell value={row.assessmentName} />
      <DaysOpenCell initiationDate={row.createdAt} />
      <TextCell value={row.dueDate} />
    </CursorTooltip>
  );
}
